import { FC, memo } from 'react'
import { useEventKindEntryForm } from './useEventKindEntryForm'
import { CrewTextBoxField } from 'components/forms/crewTextBoxField'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewErrorSummary } from 'components/forms/crewErrorSummary'
import { CrewCheckBoxField } from 'components/forms/crewCheckBoxField'
import { useGetEventKindQuery } from '@crew/apis/project/projectApis'
import { useTranslation } from '@crew/modules/i18n'
import { ComponentCallbackHandler } from '@crew/utils'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useBadgeInvertedColorClassDataSource } from 'hooks/dataSource/useResourceDataSource'
import { useShowApiErrorsWithForm } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormValues } from './useEventKindEntryForm'
import { CrewBadgeColorSelectBoxField } from 'components/forms/crewBadgeColorSelectBoxField'
export type EventKindEntryFormProps = {
  isEditMode: boolean
  eventKindId?: string
  onSubmit: () => void
  onClose: () => void
}

export const EventKindEntryForm: FC<EventKindEntryFormProps> = memo((props) => {
  const {
    control,
    reset,
    formState,
    handleSubmit,
    clearErrors,
    setError,

    validateRules,

    insertEventKind,
    updateEventKind,
    isLoadingInsertEventKind,
    isLoadingUpdateEventKind,
  } = useEventKindEntryForm()

  const { t } = useTranslation()
  const { success } = useToast()

  const [showApiErrors] = useShowApiErrorsWithForm(setError)

  // バッジプレビュー表示用項目
  const [previewBadgeName, setPreviewBadgeName] = useState<string>('')

  // イベント種別取得
  // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用し、
  // undefined時はskipTokenでAPIを実行しない
  const getEventKindParam = props.eventKindId
    ? ({
        eventKindId: props.eventKindId,
      } as const)
    : skipToken

  const { data: getEventKindQueryResult } =
    useGetEventKindQuery(getEventKindParam)

  // フォーム初期化処理関数
  const initializeForm = useCallback(() => {
    if (props.isEditMode && props.eventKindId) {
      if (getEventKindQueryResult?.eventKind) {
        reset({
          id: getEventKindQueryResult.eventKind?.id,
          name: getEventKindQueryResult.eventKind?.name,
          displayColor: getEventKindQueryResult.eventKind?.displayColor,
          initialValue: getEventKindQueryResult.eventKind?.initialValue,
          version: getEventKindQueryResult.eventKind?.version,
        })
      }
    }
  }, [
    getEventKindQueryResult?.eventKind,
    props.isEditMode,
    props.eventKindId,
    reset,
  ])

  // 初期化処理をuseEffect化
  // TODO: 本当はuseEffectを外したいのだが、レンダリングが大量に走ってしまうためこのようにしている。以下タスクで調査・対応予定
  // https://break-tmc.atlassian.net/browse/CREW-6028
  useEffect(() => {
    // フォーム初期化を実行
    initializeForm()
  }, [initializeForm])

  // バッジの色スタイルクラス用カスタムデータソース
  const colorClassListDataSource = useBadgeInvertedColorClassDataSource()

  // 名称テキスト変更時
  const handleNameChanged = useCallback<
    ComponentCallbackHandler<typeof CrewTextBoxField, 'onValueChanged'>
  >((event) => {
    setPreviewBadgeName(event.value)
  }, [])

  // 登録ボタン押下
  const handleSubmitButtonClick = useCallback(() => {
    const onSubmit = async (data: FormValues) => {
      try {
        if (props.isEditMode) {
          await updateEventKind(data)

          success(t('message.eventKind.eventKindUpdated'))
        } else {
          await insertEventKind(data)

          success(t('message.eventKind.eventKindRegistered'))
        }
        reset()
        clearErrors()
        props.onSubmit && props.onSubmit()
      } catch (err) {
        showApiErrors(err)
      }
    }
    handleSubmit(onSubmit)()
  }, [
    clearErrors,
    handleSubmit,
    insertEventKind,
    props,
    reset,
    showApiErrors,
    success,
    t,
    updateEventKind,
  ])

  // イベント種別ダイアログのキャンセルボタン押下
  const handleCancelButtonClick = useCallback(() => {
    props.onClose()
  }, [props])

  const canSend = useMemo(
    // fromState.isValidはerrorsが空でもfalseになることがあるためerrorsで判定する
    () => Object.keys(formState.errors).length === 0 && !formState.isSubmitting,
    // formStateはproxyなのでformState自体をlistenする必要がある
    // https://react-hook-form.com/api/useform/formstate
    [formState]
  )

  return (
    <form>
      <div>
        {/* event type name */}
        <div className="h-20">
          <CrewTextBoxField
            id="name"
            name="name"
            control={control}
            labelMode="hidden"
            label={t('label.name')}
            required={true}
            valueChangeEvent="keyup"
            onValueChanged={handleNameChanged}
            rules={validateRules.name}
          />
        </div>

        {/* 色選択 */}
        <div className="h-20">
          <CrewBadgeColorSelectBoxField
            id="displayColor"
            name="displayColor"
            control={control}
            labelMode="hidden"
            label={t('label.color')}
            required={true}
            dataSource={colorClassListDataSource}
            displayExpr="name"
            valueExpr="id"
            width="200px"
            rules={validateRules.displayColor}
            previewText={previewBadgeName}
          />
        </div>

        {/* initial value */}
        <div className="h-20">
          <CrewCheckBoxField
            id="initialValue"
            control={control}
            name="initialValue"
            label={t('label.initialValue')}
          />
        </div>
        <CrewErrorSummary formState={formState} />
      </div>
      <div className="flex justify-between items-center mt-3">
        <div className="ml-auto flex gap-x-5">
          <CrewButton
            text={t('action.register')}
            type="primary"
            disabled={
              !canSend || isLoadingInsertEventKind || isLoadingUpdateEventKind
            }
            onClick={handleSubmitButtonClick}
          />
          <CrewButton
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCancelButtonClick}
          />
        </div>
      </div>
    </form>
  )
})
